import type { ReactNode } from 'react'
import { Icon } from './Icon'
import type { IconName } from './icons/iconData'
import { VARIANT_COLOR, VARIANT_BORDER_CLASS } from './launcherCardVariant'
import type { LauncherCardVariant } from './launcherCardVariant'

export type { LauncherCardVariant }

/**
 * `div.card` → `launcher-card` (process/08-COMPONENT-CATALOGUE.md, 9 variants). **Built W174** —
 * every prior call site (`VHome`, `GroupLanding`, `VJobs`, `VAudit`, `VSetbackup`, `VRestore`) drew
 * its own bare, colourless, icon-less `border border-bd` list item instead, because the component
 * behind `data-cmp="launcher-card"` was never actually written to the catalogue's own spec — the
 * same "tag present, variant absent" shape `Chip.tsx` had until W172, and `l1_conform.py` cannot
 * catch it for the identical reason (it checks tag presence per screen, never variant coverage).
 *
 * `border-top:3px solid var(--{color})` is the whole visual identity of the 7 colour variants
 * (`.g-green`/`.g-teal`/`.g-indigo`/`.g-magenta`/`.g-orange`/`.g-blue`/`.g-off`), each reusing the
 * raw `--green`/`--teal`/etc. custom properties already carried over verbatim from the prototype's
 * own `:root` (11-TOKEN-MAP.md) — **not** the six `--accent-*` tokens minted at W139 "for the
 * launcher-card accents", which turn out to hold different hex values from the prototype's own
 * `.g-*` rules and cover neither `magenta` nor `orange`; that minting is flagged as its own defect
 * in `process/11-TOKEN-MAP.md` rather than pressed into service here to make two wrong colours fit.
 * `border-t-4` (Tailwind's default width scale has no `3`) is a 1px delta, the same tolerance
 * `Icon.tsx`'s own sizing already accepted.
 *
 * `disabled` (`.is-disabled`: `opacity:.55;filter:saturate(.5)`, and no anchor rendered at all in
 * place of the prototype's own `pointer-events:none`) is the ONE card in the whole app that needs
 * it today (`v-audit`'s "Bitbucket Audit Logs", `Planned`).
 *
 * **The whole `div.card` is the clickable surface, matching the prototype's own `onclick` on the
 * div** — the title stays a real `<a>` (same `data-act`, same accessible name) so tab+Enter still
 * reaches it directly; its own `onClick` only calls `preventDefault()` and lets the click bubble to
 * the div's handler, which is what actually navigates. That keeps a single source of truth for the
 * navigation (no double-fire between the anchor and the div) while giving the card the prototype's
 * full-surface click target plus the keyboard affordance the prototype itself never had.
 */
export function LauncherCard({
  variant,
  icon,
  title,
  description,
  disabled,
  badge,
  onClick,
  actionId,
}: {
  variant: LauncherCardVariant
  icon: IconName
  title: string
  description: ReactNode
  disabled?: boolean
  badge?: ReactNode
  onClick?: () => void
  actionId?: string
}) {
  const clickable = Boolean(onClick) && !disabled
  return (
    <div
      data-cmp="launcher-card"
      className={`bg-panel border border-bd rounded-2 px-7 py-6 border-t-4 ${VARIANT_BORDER_CLASS[variant]} ${disabled ? 'opacity-55 [filter:saturate(.5)]' : ''} ${clickable ? 'cursor-pointer hover:shadow-sm' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <h4 className="m-0 mb-2 flex items-center gap-2 text-body font-semibold">
        <Icon name={icon} color={VARIANT_COLOR[variant]} />
        {clickable ? (
          <a href="#" data-act={actionId} onClick={(e) => e.preventDefault()} className="text-link">
            {title}
          </a>
        ) : (
          title
        )}
        {badge}
      </h4>
      <p className="m-0 text-small text-tx2">{description}</p>
    </div>
  )
}
